import { useCallback, useState } from 'react'
import { Table, type Column } from 'grabbitbot-legacy-ui'
import { adminClient } from '../../api'
import type { LogEventItem, LogEventsAnswer } from '../../api/adminTypes'
import { formatDateTime } from '../trading/tradingConstants'
import { AdminNotice } from './adminShared'
import { describeAdminError, redactedJson } from './adminHelpers'
const DATE_MODES = ['last1Hour', 'last3Hours', 'last6Hours', 'last12Hours', 'last24Hours', 'last48Hours', 'manual']
const DEFAULT_PAGE_SIZE = 100

/**
 * Equivalent of legacy `SearchEventLogComponent` (route `admin/log`,
 * contract #9). `pageSize` is ALWAYS sent — legacy's `LIMIT ((int)$x) ?: 100`
 * means an omitted one silently returns zero rows while still claiming
 * `pageSize: 100` in the answer, the kind of quirk a client can only avoid
 * by never triggering it.
 */
export function AdminLogPage() {
  const [dateMode, setDateMode] = useState('last24Hours')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const [order, setOrder] = useState<'dateasc' | 'datedesc'>('datedesc')
  const [email, setEmail] = useState('')
  const [hideAlg, setHideAlg] = useState(false)
  const [answer, setAnswer] = useState<LogEventsAnswer | null>(null)
  const [feedback, setFeedback] = useState<ReturnType<typeof describeAdminError> | undefined>()

  const search = useCallback(
    (targetPage: number) => {
      setAnswer(null)
      setFeedback(undefined)
      adminClient
        .logEvents({
          date: dateMode,
          pageSize,
          page: targetPage,
          order,
          email: email || undefined,
          hideAlg,
          from: dateMode === 'manual' ? from : undefined,
          to: dateMode === 'manual' ? to : undefined,
        })
        .then((result) => {
          setAnswer(result)
          setPage(targetPage)
        })
        .catch((err) => setFeedback(describeAdminError(err)))
    },
    [dateMode, pageSize, order, email, hideAlg, from, to],
  )

  const columns: Column<LogEventItem>[] = [
    { key: 'date', label: 'Date', align: 'left', sortValue: (r) => r.date, format: (v) => formatDateTime(v as string) },
    { key: 'email', label: 'E-mail', align: 'left', sortValue: (r) => r.email },
    { key: 'element', label: 'Element', align: 'left', sortValue: (r) => r.element },
    { key: 'url', label: 'URL', align: 'left', sortValue: (r) => r.url ?? '' },
    {
      key: 'params',
      label: 'Params',
      sortable: false,
      format: (_v, r) => <code className="portal-text-muted">{redactedJson(r.params ?? r.paramsFormatted)}</code>,
    },
  ]

  return (
    <div>
      <div className="portal-page-title">
        <h1>Admin — event log</h1>
      </div>
      <div className="portal-card">
        <form
          className="portal-form"
          onSubmit={(e) => {
            e.preventDefault()
            search(1)
          }}
        >
          <label htmlFor="log-date">Window</label>
          <select id="log-date" value={dateMode} onChange={(e) => setDateMode(e.target.value)}>
            {DATE_MODES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          {dateMode === 'manual' && (
            <>
              <label htmlFor="log-from">From</label>
              <input id="log-from" type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
              <label htmlFor="log-to">To</label>
              <input id="log-to" type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
            </>
          )}
          <label htmlFor="log-email">E-mail (optional filter)</label>
          <input id="log-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="alice@example.com" />
          <label htmlFor="log-page-size">Page size</label>
          <input
            id="log-page-size"
            type="number"
            min={1}
            value={pageSize}
            onChange={(e) => setPageSize(Math.max(1, Number(e.target.value) || DEFAULT_PAGE_SIZE))}
          />
          <label htmlFor="log-order">Order</label>
          <select id="log-order" value={order} onChange={(e) => setOrder(e.target.value as 'dateasc' | 'datedesc')}>
            <option value="datedesc">Newest first</option>
            <option value="dateasc">Oldest first</option>
          </select>
          <label htmlFor="log-hide-alg">
            <input id="log-hide-alg" type="checkbox" checked={hideAlg} onChange={(e) => setHideAlg(e.target.checked)} /> Hide algorithm
            traffic
          </label>
          <button type="submit">Search</button>
        </form>
        <AdminNotice feedback={feedback} />
        {answer && answer.error && <p className="portal-notice portal-notice--warning">{answer.error}</p>}
        {answer && !answer.error && (
          <>
            <h3>
              {answer.items.length} rows — page {answer.page ?? page}, page size {answer.pageSize ?? pageSize}
            </h3>
            <div className="portal-table-scroll">
              <Table columns={columns} rows={answer.items} rowKey={(r, i) => `${r.insertId ?? r.email}-${r.date}-${i}`} />
            </div>
            <nav className="gbx-pager" aria-label="Pagination">
              <button type="button" disabled={page <= 1} onClick={() => search(page - 1)}>
                Previous
              </button>
              <span>Page {page}</span>
              <button type="button" disabled={answer.items.length < pageSize} onClick={() => search(page + 1)}>
                Next
              </button>
            </nav>
          </>
        )}
      </div>
    </div>
  )
}
